import React from "react";
import {
  getHeadingField,
  getLinkFields,
  getMapLink,
  getRegionFields,
  getTagField,
  getTextFields,
} from "./SearchProviderTool";
import { SearchProviderConfiguration, Provider } from "./SearchProvider.types";
import { useEffect, useRef } from "react";

type ProviderListItemProps = {
  provider: Provider;
  selected: boolean;
  configuration: SearchProviderConfiguration;
  onClick: (provider: Provider) => void;
};

const ProviderListItem: React.FC<ProviderListItemProps> = ({
  provider,
  configuration,
  selected,
  onClick,
}) => {
  const itemRef = useRef<HTMLLIElement>(null);

  const heading = getHeadingField(provider),
    textFields = getTextFields(provider),
    linkFields = getLinkFields(provider),
    regionFields = getRegionFields(provider),
    tagField = getTagField(provider);

  useEffect(() => {
    if (selected && itemRef.current) {
      // scroll into view
      itemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selected, itemRef]);

  const hasTags =
    tagField && Array.isArray(tagField.value) && tagField.value.length > 0;

  const hasRegionTravelOrTags =
    (provider.canTravel && configuration.travelMessage) ||
    (regionFields && regionFields.length > 0 && configuration.showCovers) ||
    hasTags;

  return (
    <li
      ref={itemRef}
      key={provider.id}
      className={`provider__item ${selected ? "provider__item--selected" : ""}`}
      data-provider-id={provider.id}
      onClick={() => onClick(provider)}
    >
      <div className="provider__item__heading">
        {heading && <h6>{heading}</h6>}
        {textFields.map((field, idx) => {
          return <p key={idx}>{field.label}</p>;
        })}
      </div>

      {hasRegionTravelOrTags && (
        <div className="provider__item__stack provider__item__stack--first">
          {provider.canTravel && configuration.travelMessage && (
            <p className="provider__item__travel">
              {configuration.travelMessage}
            </p>
          )}

          {regionFields &&
          regionFields.length > 0 &&
          configuration.showCovers ? (
            <div className="provider__item__regions">
              {regionFields.map((field, idx) => (
                <p key={idx}>
                  <strong>{field.label}:</strong>&nbsp;
                  {Array.isArray(field.value) &&
                    field.value.map((region, idx: number) => (
                      <span key={region.title}>
                        {region.title}
                        {comma(idx, field.value)}
                      </span>
                    ))}
                </p>
              ))}
            </div>
          ) : null}

          {hasTags && (
            <ul className="provider__item__tags">
              {Array.isArray(tagField.value) &&
                tagField.value.map((tag, idx) => (
                  <li key={idx}>{String(tag)}</li>
                ))}
            </ul>
          )}
        </div>
      )}

      <div className="provider__item__stack provider__item__stack--last">
        {linkFields.map((field, idx) => {
          const fieldConfiguration = configuration.fields.find(
            (filter) => filter.id === field.configurationId
          );

          if (field.type === "address") {
            if (
              fieldConfiguration &&
              !fieldConfiguration.showOnMainResultCard
            ) {
              return null;
            }

            return (
              <p
                key={idx}
                style={{ display: "flex" }}
                data-configuration={field.configurationId}
              >
                <a
                  href={getMapLink(field)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="provider__item__link"
                  data-type={field.type}
                >
                  <span>{field.label}</span>
                </a>
              </p>
            );
          }

          const linkHref = field.value ? field.value["url"] : field.value;

          let icon: string = "";

          if (fieldConfiguration && fieldConfiguration.icon) {
            icon = fieldConfiguration.icon;
          }

          if (!field.label || !linkHref) return;

          return (
            <p key={idx} style={{ display: "flex" }}>
              <a
                href={linkHref}
                target="_blank"
                rel="noopener noreferrer"
                className="provider__item__link"
                data-type={field.type}
              >
                {icon && (
                  <i
                    className={`icon `}
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: icon }}
                  ></i>
                )}
                <span>{field.label}</span>
              </a>
            </p>
          );
        })}
      </div>
    </li>
  );
};

const comma = (index, arr) => {
  return index === arr.length - 1 ? "" : ", ";
};

export default ProviderListItem;
